import type {
  GanttMilestone,
  MilestoneStatus,
  RunwayMilestone,
  SeverityRow,
  TrendPoint,
} from '@/components/ui/charts';
import { POSTURE_LABEL, VERDICT_LABEL } from '@/components/ui/charts';
import type {
  HealthReadinessView,
  HrCriterion,
  HrCriterionGroup,
  HrOpenByDomain,
} from '@/lib/api/health-readiness';
import type { MilestoneStatusBackend, ScheduleMilestone } from '@/lib/api/health-schedule';
import { dateValue, formatDate } from '@/lib/utils/format';

/**
 * What the Overview draws, read off the composite read and the schedule
 * list (T-0242 / E22-F01). Pure, so the cards stay markup and these stay
 * tested.
 *
 * Integrity rule (E22 design §6.3): no rollup in the browser. Every total,
 * count, band, verdict, contribution, slip and trend change shown is the
 * API's. What happens here is selection, ordering, unit conversion (a
 * weight fraction as points), a value's distance from a gate or from its
 * own maximum, and calendar arithmetic on API dates (days to go-live, how
 * much of the timeline has passed). The one count is the number of
 * milestones whose API status is slipped or at risk, read off the rows
 * the road chart draws.
 */

const DAY = 86_400_000;

/** A local calendar date as the API writes one: "2026-10-10". */
export function localDayIso(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * A brand-new engagement: nothing rated, nothing scored, no snapshot. The
 * get-started card replaces the trend row (prototype `overviewTab()`).
 */
export function isNewEngagement(view: HealthReadinessView): boolean {
  const counts = view.verdict.posture.counts;
  return (
    view.reports.snapshots.count === 0 &&
    view.trend.length === 0 &&
    (view.verdict.readiness?.scoredCount ?? 0) === 0 &&
    counts.unassessed === counts.total
  );
}

/** Whole days from today to a calendar date; negative once it has passed. */
export function daysUntil(iso: string, today: Date): number {
  return Math.round((dateValue(iso) - startOfDay(today)) / DAY);
}

/** How much of kickoff → go-live has passed, 0..100; null without a span. */
export function timelineElapsed(start: string, end: string, today: Date): number | null {
  const a = dateValue(start);
  const b = dateValue(end);
  if (!(b > a)) return null;
  const pct = ((startOfDay(today) - a) / (b - a)) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

/**
 * How far the weighted total sits from the gates, on the value the gauge
 * prints (whole points), so the words agree with the number.
 */
export function gateDistance(
  totalPercent: number,
  thresholds: { go: number; conditional: number },
): { toGo: number; toConditional: number } {
  const shown = Math.round(totalPercent);
  return { toGo: thresholds.go - shown, toConditional: thresholds.conditional - shown };
}

/** "+26", "−3", "+1.5": a change in points as the trend card prints it. */
export function formatChange(points: number): string {
  const n = Math.round(points * 10) / 10;
  const text = Number.isInteger(n) ? String(Math.abs(n)) : Math.abs(n).toFixed(1);
  return `${n < 0 ? '−' : '+'}${text}`;
}

/** Points as the "where the points are" card prints them: 15, 12.5. */
export function formatPoints(points: number): string {
  const n = Math.round(points * 10) / 10;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// ---------------------------------------------------------------------------
// The AI readings waiting
// ---------------------------------------------------------------------------

export interface PendingReading {
  kind: 'domain' | 'criterion';
  id: string;
  name: string;
  /** "rate Amber", "score 45%". */
  reading: string;
  confidence: number;
}

/** The domains with a pending rating proposal, then the criteria with a pending score. */
export function pendingReadings(view: HealthReadinessView): PendingReading[] {
  const out: PendingReading[] = [];
  for (const d of view.domains) {
    if (!d.aiProposal) continue;
    out.push({
      kind: 'domain',
      id: d.id,
      name: d.name,
      reading: `rate ${POSTURE_LABEL[d.aiProposal.status]}`,
      confidence: d.aiProposal.confidence,
    });
  }
  for (const c of view.criteria) {
    if (!c.aiCandidate) continue;
    const score =
      c.aiCandidate.scorePercent !== null
        ? `${Math.round(c.aiCandidate.scorePercent)}%`
        : String(c.aiCandidate.score);
    out.push({
      kind: 'criterion',
      id: c.id,
      name: c.name,
      reading: `score ${score}`,
      confidence: c.aiCandidate.confidence,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Readiness over time
// ---------------------------------------------------------------------------

function verdictWords(recommendation: keyof typeof VERDICT_LABEL | null): string {
  return recommendation ? VERDICT_LABEL[recommendation] : 'Not all scored';
}

/**
 * The trend card's points: each snapshot's frozen total, then today's live
 * total when something is scored and no snapshot was taken today
 * (prototype `readinessPoints()`).
 */
export function readinessTrendPoints(view: HealthReadinessView, today: Date): TrendPoint[] {
  const points: TrendPoint[] = view.trend
    .filter((t) => t.totalPercent !== null)
    .map((t) => ({
      date: t.capturedAt,
      value: t.totalPercent as number,
      sub: `${verdictWords(t.recommendation)} · posture ${POSTURE_LABEL[t.posture.status]}`,
    }));
  const readiness = view.verdict.readiness;
  const todayIso = localDayIso(today);
  const last = points[points.length - 1];
  const snappedToday = last ? formatDate(last.date) === formatDate(todayIso) : false;
  if (readiness && readiness.scoredCount > 0 && readiness.totalPercent !== null && !snappedToday) {
    points.push({
      date: todayIso,
      value: readiness.totalPercent,
      live: true,
      sub: `${verdictWords(readiness.recommendation)} · posture ${POSTURE_LABEL[view.verdict.posture.status]}`,
    });
  }
  return points;
}

// ---------------------------------------------------------------------------
// Where the points are
// ---------------------------------------------------------------------------

export interface CategoryPoints {
  category: string;
  /** Points the category earns, the API's aggregate (0 when none). */
  earned: number;
  /** Points on offer: the category's weight as points of 100. */
  available: number;
  firstCriterionId: string | null;
}

/** Each category's earned and available points, the largest gap first. */
export function categoryPoints(groups: HrCriterionGroup[]): CategoryPoints[] {
  return groups
    .filter((g) => g.weight !== null && g.weight > 0)
    .map((g) => ({
      category: g.category,
      earned: g.aggregatePercent ?? 0,
      // A fraction as points of 100, without the float noise (0.23 → 23).
      available: Math.round((g.weight as number) * 10_000) / 100,
      firstCriterionId: g.criteria[0]?.id ?? null,
    }))
    .sort((a, b) => b.available - b.earned - (a.available - a.earned));
}

export interface CriterionGap {
  criterion: HrCriterion;
  /** Points it would add at 100%: the rest of its score, times its weight. */
  missing: number;
}

/**
 * The scored criteria furthest from full marks, by the points they would
 * add at 100% (prototype `readiness().gaps`): one criterion's own score
 * and weight, no sum.
 */
export function largestGaps(criteria: HrCriterion[], limit = 3): CriterionGap[] {
  return criteria
    .filter((c) => c.scorePercent !== null)
    .map((c) => ({ criterion: c, missing: (100 - (c.scorePercent as number)) * c.weight }))
    .filter((g) => g.missing >= 0.05)
    .sort((a, b) => b.missing - a.missing)
    .slice(0, limit);
}

// ---------------------------------------------------------------------------
// Findings by domain
// ---------------------------------------------------------------------------

/** One domain's open findings by severity, as the API sent them. */
export function openFindingsOf(view: HealthReadinessView, domainId: string): HrOpenByDomain | null {
  return view.evidence.openByDomain.find((r) => r.domainId === domainId) ?? null;
}

/** The findings card's rows: the API's rows, named; '' keys the findings with no domain. */
export function severityRows(view: HealthReadinessView): SeverityRow[] {
  const names = new Map<string, string>();
  for (const d of view.retiredDomains) names.set(d.id, `${d.name} (retired)`);
  for (const d of view.domains) names.set(d.id, d.name);
  return view.evidence.openByDomain
    .filter((r) => r.total !== null)
    .map((r) => ({
      id: r.domainId ?? '',
      name: r.domainId === null ? 'No domain' : (names.get(r.domainId) ?? 'Retired domain'),
      counts: { critical: r.critical, high: r.high, medium: r.medium, low: r.low },
      total: r.total as number,
    }));
}

// ---------------------------------------------------------------------------
// Milestones
// ---------------------------------------------------------------------------

const MILESTONE_STATUS: Record<MilestoneStatusBackend, MilestoneStatus> = {
  done: 'done',
  on_track: 'on-track',
  at_risk: 'at-risk',
  slipped: 'late',
};

/** Where a milestone lands: its actual date once done, else its forecast, else the plan. */
function projectedDate(m: ScheduleMilestone): string {
  if (m.status === 'done') return m.actualDate ?? m.forecastDate ?? m.plannedDate;
  return m.forecastDate ?? m.plannedDate;
}

export function toGanttMilestone(m: ScheduleMilestone): GanttMilestone {
  return {
    id: m.registerEntryId,
    name: m.name,
    planned: m.plannedDate,
    projected: projectedDate(m),
    status: MILESTONE_STATUS[m.status],
    slipDays: m.slipDaysBackend,
    gate: m.isIvvGate,
  };
}

/**
 * The runway's dots: every milestone but the go-live itself (the diamond
 * at the end draws it), found by its plan matching the planned go-live.
 */
export function runwayMilestones(
  milestones: ScheduleMilestone[],
  plannedGoLive: string | null,
): RunwayMilestone[] {
  return milestones
    .filter((m) => !(plannedGoLive && formatDate(m.plannedDate) === formatDate(plannedGoLive)))
    .map((m) => ({
      id: m.registerEntryId,
      name: m.name,
      date: projectedDate(m),
      status: MILESTONE_STATUS[m.status],
    }));
}

/**
 * The countdown card's line: milestones still open whose projection sits
 * after the plan (the API's slip), and IV&V gates the API does not call on
 * track.
 */
export function milestoneSummary(milestones: ScheduleMilestone[]): {
  slipped: number;
  gatesAtRisk: number;
} {
  const open = milestones.filter((m) => m.status !== 'done');
  return {
    slipped: open.filter((m) => m.slipDaysBackend > 0).length,
    gatesAtRisk: open.filter((m) => m.isIvvGate && m.status !== 'on_track').length,
  };
}
